/**
 * Single source of truth for a user's Firestore document (Users/{uid}).
 *
 * The same keys are:
 *   - copied by App.svelte (updatePrefs) and validated by Config.svelte (validatePrefs);
 *     every key must be present or validation fails and the user is sent to Settings;
 *   - read by FriendList.svelte (`email`, `friends`);
 *   - created by Login.svelte at sign-up via newUserDocument().
 */
export const defaultPrefs = {
  name: "",
  email: "",
  videoSource: "auto",
  audioSource: "auto",
  debug: false,
  fullscreen: false,
  minimumFaceScore: 80,
  faceDetectionSlack: 15004,
  faceDetectionInterval: 1004,
  detectionResolution: "640x480",
  transmitFramerate: 24,
  transmitResolution: "1280x720", // 1080p software encoding causes visible frame drops on ordinary PCs
  selfView: false,
  dnd: false,
  // Play a short chime when a window opens (the overlay still shows either way).
  chime: true,
  // Keep a quiet connection to friends who are online so a window opens the moment
  // both sides are seen. Off: match on the server first, then connect (slower).
  keepReady: true,
  // Testing aid: route the call through the TURN relay only, ignoring direct
  // routes, so a test on one network behaves like two devices far apart.
  forceRelay: false,
  // Which video codec to ask for first. "auto" is VP9 > H.264 > VP8; the others put
  // that codec first. Useful when one device's hardware decoder misbehaves.
  videoCodec: "auto",
  // Testing aid: how the face detector gets camera frames. "stream" hands the camera
  // frame stream to the detector worker; "copy" copies a frame from the hidden preview
  // each check; "pause" stops the detector touching the camera while a window is open.
  detectorPath: "stream",
};

/**
 * Build the Users/{uid} document for a newly registered account.
 * `friends` is the list of friend uids FriendList.svelte binds to its multi-select;
 * `createdAt` should be firebase.firestore.FieldValue.serverTimestamp().
 */
export function newUserDocument({ name, email, createdAt }) {
  return {
    ...defaultPrefs,
    name,
    email,
    friends: [],
    createdAt,
  };
}
